"use client";

import React from "react";
import { Layers, ShieldCheck, Gauge, ArrowRightLeft, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useLanguage } from "@/hooks/useLanguage";
import { pick, type Lang } from "@/lib/i18n";
interface TranslationStrings {
  badge: string;
  title: string;
  intro: string;
  cards: {
    title: string;
    sub: string;
    description: string;
    specs: string[];
  }[];
  tableTitle: string;
  tableSubtitle: string;
  tableHeaders: {
    param: string;
    optA: string;
    optB: string;
    verdict: string;
  };
  tableRows: {
    param: string;
    optA: string;
    optANote: string;
    optB: string;
    optBNote: string;
    verdict: string;
  }[];
}
const CONTENT: Record<Lang, TranslationStrings> = {
  en: {
    badge: "ENGINEERING REFERENCE",
    title: "Engineering Comparison Highlights",
    intro: "This technical guide assists procurement teams and filtration engineers in evaluating critical trade-offs across weave geometry, alloy corrosion ratings, and structural composite formats.",
    cards: [{
      title: "Weave Geometry",
      sub: "Flow vs. Particle Retention",
      description: "Flow dynamics and filtration cutoff thresholds depend directly on aperture stability across plain, twill, plain Dutch, and reverse Dutch patterns.",
      specs: ["Plain & Twill: High open area (30–65%) for low differential pressure coarse straining.", "Plain Dutch: Compact warp spacing for accurate 10–300 µm solid retention.", "Reverse Dutch: High count warp wire array engineered for high tensile mechanical stress in continuous screeners."]
    }, {
      title: "Alloy Metallurgy",
      sub: "Acid, Chloride & Thermal Limits",
      description: "Selecting optimal stainless steel and high-nickel alloys prevents premature crevice attack, pitting, and embrittlement under aggressive media conditions.",
      specs: ["AISI 304 / 316L: General industrial food, sanitary, and light pharmaceutical standards.", "High-Moly Stainless: Enhanced molybdenum alloy chemistry for acidic chemical wash cycles.", "Nickel-Chromium Alloys: Specialty grade options for severe wet halogens and organic acids."]
    }, {
      title: "Structural Formats",
      sub: "Single-Ply vs. Sintered Composites",
      description: "Balancing mechanical rigidity against backwash cleanability between single-layer mesh cloth and multi-layer diffusion-bonded sintered laminates.",
      specs: ["Single-Ply Wire Cloth: Cost-effective, flexible formation for disposable elements and light pleated cartridges.", "3-Layer Sintered: Fine filter cloth protected by diffusion-bonded protective and support wire matrices.", "5-Layer Standard Sintered: Rigid monolithic plate resisting collapse under cyclic reverse flow."]
    }],
    tableTitle: "Direct Specification Comparison",
    tableSubtitle: "ISO 9044 / ASTM E2016 Compliant Specifications",
    tableHeaders: {
      param: "Evaluation Parameter",
      optA: "Option A (Single-Ply / Standard)",
      optB: "Option B (Sintered / Reinforced)",
      verdict: "Engineering Verdict"
    },
    tableRows: [{
      param: "Filtration Precision",
      optA: "Plain Dutch Weave",
      optANote: "10–300 µm nominal",
      optB: "Sintered Multi-Layer",
      optBNote: "1–200 µm precision",
      verdict: "Dutch weave offers high throughput; sintered multi-layer adds backwash rigidity."
    }, {
      param: "Operating Pressure",
      optA: "Standard Single-Ply Cloth",
      optANote: "Low ΔP (unsupported)",
      optB: "Reinforced Sintered Laminate",
      optBNote: "High ΔP structural stability",
      verdict: "Multi-layer diffusion-bonded composites withstand cyclic differential loads."
    }, {
      param: "Corrosion in Chlorides",
      optA: "AISI 304 / 304L Stainless Steel",
      optANote: "Standard industrial rating",
      optB: "AISI 316L / High-Alloy",
      optBNote: "Molybdenum-bearing grade",
      verdict: "Selecting 316L or higher molybdenum alloys prevents pitting in aggressive chloride exposure."
    }]
  },
  zh: {
    badge: "工程选型参考",
    title: "工程对比要点",
    intro: "本技术指南旨在协助工程采购团队与过滤工程师，在编织几何结构、合金耐腐蚀等级以及多层结构形态之间做出明确的技术权衡判断。",
    cards: [{
      title: "编织结构",
      sub: "流速与截留精度权衡",
      description: "平纹、斜纹、平纹密纹（荷兰编织）以及反向密纹结构的孔隙稳定性，直接决定流体阻力与颗粒截留分水岭。",
      specs: ["平纹与斜纹：高开孔率（30–65%），适用于低压差粗过滤及物料初筛。", "平纹密纹：经密纬紧排列，实现10–300微米稳定精度并保持合理通量。", "反向密纹：高强度经线排布，专为立式连续换网机的高机械张力环境设计。"]
    }, {
      title: "合金冶金特性",
      sub: "酸液、氯离子及温控极限",
      description: "针对严苛化学环境合理配置不锈钢与特种耐蚀合金，杜绝缝隙腐蚀、点蚀和晶间腐蚀失效风险。",
      specs: ["304 / 316L 不锈钢：通用工业、食品加工及常规过滤介质的标准选材。", "高钼不锈钢牌号：增强钼元素配方，抵御酸性冲洗与氯离子点蚀。", "特种耐蚀合金：针对严苛湿态卤素及高腐蚀性有机酸混合介质提供工程方案。"]
    }, {
      title: "结构形态",
      sub: "单层丝网 vs 烧结层叠板",
      description: "对比单层编织网与扩散焊接多层金属烧结网在机械刚性、可重复反冲洗性能及抗压溃能力上的差异。",
      specs: ["单层工业金属网：成本受控，延展性佳，适配一次性滤芯或折叠滤筒制作。", "3层烧结复合网：在精细过滤网两侧经真空扩散焊接保护层与骨架支撑层。", "5层标准烧结网：整体烧结成坚固多孔板，抵御反复反冲洗脉冲冲击而不变形。"]
    }],
    tableTitle: "核心技术指标对比",
    tableSubtitle: "符合 ISO 9044 / ASTM E2016 制造执行规范",
    tableHeaders: {
      param: "评估参数",
      optA: "方案 A (单层 / 常规)",
      optB: "方案 B (烧结 / 强化结构)",
      verdict: "工程选型结论"
    },
    tableRows: [{
      param: "过滤精度对比",
      optA: "平纹荷兰密纹网",
      optANote: "10–300 µm 公称精度",
      optB: "标准烧结金属多层网",
      optBNote: "1–200 µm 高精度稳定",
      verdict: "荷兰密纹网通量更高；烧结多层板显著增强反冲洗结构刚性。"
    }, {
      param: "工作压差耐受",
      optA: "常规单层编织网",
      optANote: "低工作压差 (无骨架支撑)",
      optB: "强化型多层烧结板",
      optBNote: "高差压结构稳定性",
      verdict: "多层扩散焊接复合结构可承受严苛交变脉冲与高压差负荷。"
    }, {
      param: "氯化物耐腐蚀性",
      optA: "AISI 304 / 304L 不锈钢",
      optANote: "常规工业耐蚀等级",
      optB: "AISI 316L / 高耐蚀合金",
      optBNote: "含钼耐点蚀牌号",
      verdict: "优先选用 316L 或高耐蚀等级，有效防止含氯介质中的点蚀失效。"
    }]
  },
  es: {
    badge: "REFERENCIA DE INGENIERÍA",
    title: "Aspectos destacados de la comparación técnica",
    intro: "Esta guía técnica asiste a equipos de compras e ingenieros de filtración a evaluar compensaciones críticas en geometría de tejido, aleaciones y formatos compuestos.",
    cards: [{
      title: "Geometría de tejido",
      sub: "Caudal vs. retención de partículas",
      description: "La dinámica de flujo y el corte de filtración dependen directamente de la estabilidad de abertura en patrones lisos, sarga, Dutch y Dutch inverso.",
      specs: ["Liso y sarga: Alta área abierta (30–65%) para separación gruesa a baja presión diferencial.", "Dutch liso: Disposición compacta para retención precisa de 10–300 µm.", "Dutch inverso: Alta densidad de alambres de urdimbre para alta tensión mecánica en cribado continuo."]
    }, {
      title: "Metalurgia de aleaciones",
      sub: "Límites frente a ácidos y cloruros",
      description: "La selección adecuada de aceros inoxidables previene corrosión por picaduras y grietas en medios agresivos.",
      specs: ["AISI 304 / 316L: Estándares para industria general, alimentaria y sanitaria.", "Inoxidable con molibdeno: Mayor resistencia química en ciclos de lavado ácido.", "Aleaciones especiales: Opciones de alto rendimiento para halógenos y ácidos orgánicos."]
    }, {
      title: "Formatos estructurales",
      sub: "Malla simple vs. compuestos sinterizados",
      description: "Equilibrio entre rigidez mecánica y capacidad de retrolavado entre mallas monocapa y laminados sinterizados multicapa.",
      specs: ["Malla monocapa: Solución flexible y económica para elementos desechables y cartuchos plegados.", "Sinterizado de 3 capas: Malla fina de filtración protegida por matrices de soporte soldadas por difusión.", "Sinterizado estándar de 5 capas: Placa monolítica rígida resistente a colapsos bajo retrolavado."]
    }],
    tableTitle: "Comparación directa de especificaciones",
    tableSubtitle: "Especificaciones conformes a ISO 9044 / ASTM E2016",
    tableHeaders: {
      param: "Parámetro evaluado",
      optA: "Opción A (Monocapa / Estándar)",
      optB: "Opción B (Sinterizado / Reforzado)",
      verdict: "Conclusión de ingeniería"
    },
    tableRows: [{
      param: "Precisión de filtración",
      optA: "Tejido Dutch liso",
      optANote: "10–300 µm nominal",
      optB: "Multicapa sinterizado",
      optBNote: "1–200 µm de precisión",
      verdict: "El tejido Dutch ofrece alto caudal; el sinterizado añade rigidez para retrolavado."
    }, {
      param: "Presión de trabajo",
      optA: "Malla monocapa estándar",
      optANote: "Baja ΔP (sin soporte)",
      optB: "Laminado sinterizado reforzado",
      optBNote: "Alta estabilidad estructural bajo ΔP",
      verdict: "Los compuestos soldados por difusión resisten cargas diferenciales cíclicas."
    }, {
      param: "Corrosión en cloruros",
      optA: "Acero inoxidable AISI 304 / 304L",
      optANote: "Grado industrial estándar",
      optB: "AISI 316L / Grado superior",
      optBNote: "Grado con molibdeno",
      verdict: "El uso de 316L u opciones de mayor aleación evita picaduras en presencia de cloruros."
    }]
  },
  de: {
    badge: "TECHNISCHE REFERENZ",
    title: "Wichtigste Vergleichsaspekte der Auslegung",
    intro: "Dieser Leitfaden unterstützt Beschaffung und Ingenieure bei der Abwägung von Webgeometrie, Legierungsbeständigkeit und strukturellen Verbundformaten.",
    cards: [{
      title: "Webgeometrie",
      sub: "Durchfluss vs. Partikelrückhalt",
      description: "Strömungsdynamik und Trenngrenzen hängen von der Maschenstabilität bei Quadrat-, Köper-, Tressen- und Panzertressengeweben ab.",
      specs: ["Leinwand- & Köperbindung: Hohe offene Siebfläche (30–65%) für Grobfiltration bei geringem Druckverlust.", "Glatte Tresse: Dichte Kettanordnung für zuverlässigen Rückhalt von 10–300 µm.", "Panzertresse: Hohe Zugfestigkeit für kontinuierliche Siebprozesse unter mechanischer Beanspruchung."]
    }, {
      title: "Metallurgische Werkstoffe",
      sub: "Säure-, Chlorid- & Temperaturgrenzen",
      description: "Die optimale Legierungswahl verhindert Lochfraß und Spaltkorrosion in aggressiven Prozessmedien.",
      specs: ["AISI 304 / 316L: Bewährte Standards für Industrie, Lebensmittel und Pharmazie.", "Molybdänhaltiger Edelstahl: Erhöhte Säurebeständigkeit bei chemischen Reinigungsschritten.", "Sonderlegierungen: Optionen für aggressive Halogene und organische Säuren."]
    }, {
      title: "Strukturelle Formate",
      sub: "Einlagiges Gewebe vs. Sinterverbund",
      description: "Abwägung mechanischer Steifigkeit gegen Rückspülbarkeit zwischen Einzellagen und diffusionsgeschweißten Laminaten.",
      specs: ["Einlagiges Drahtgewebe: Wirtschaftlich, formbar für Einwegelemente und Faltfilterkerzen.", "3-lagiges Sintergewebe: Feines Filtergewebe durch diffusionsverschweißte Schutz- und Stützlagen gesichert.", "5-lagiges Standardsintergewebe: Formstabile poröse Platte gegen Kollaps bei zyklischem Rückspülen."]
    }],
    tableTitle: "Direkter Spezifikationsvergleich",
    tableSubtitle: "Spezifikationen nach ISO 9044 / ASTM E2016",
    tableHeaders: {
      param: "Bewertungsparameter",
      optA: "Option A (Einlagig / Standard)",
      optB: "Option B (Sinterverbund / Verstärkt)",
      verdict: "Technisches Fazit"
    },
    tableRows: [{
      param: "Filtrationsfeinheit",
      optA: "Glatte Tresse",
      optANote: "10–300 µm nominell",
      optB: "Mehrlagiger Sinterverbund",
      optBNote: "1–200 µm Präzision",
      verdict: "Tressengewebe bietet hohen Durchsatz; Sinterverbund liefert maximale Rückspülfestigkeit."
    }, {
      param: "Betriebsdruck",
      optA: "Einlagiges Standardgewebe",
      optANote: "Geringes ΔP (ungestützt)",
      optB: "Verstärktes Sinterlaminat",
      optBNote: "Hohe Stabilität bei hohem ΔP",
      verdict: "Diffusionsgeschweißte Verbundstrukturen widerstehen zyklischen Differenzdrücken zuverlässig."
    }, {
      param: "Korrosion in Chloriden",
      optA: "Edelstahl AISI 304 / 304L",
      optANote: "Standard-Industriequalität",
      optB: "AISI 316L / Höherlegiert",
      optBNote: "Molybdänlegierte Güte",
      verdict: "Die Wahl von 316L oder höher legierten Güten verhindert Lochfraß bei Chlorideinwirkung."
    }]
  },
  fr: {
    badge: "RÉFÉRENCE D'INGÉNIERIE",
    title: "Points clés de comparaison technique",
    intro: "Ce guide technique assiste les équipes d'achat et les ingénieurs pour évaluer les compromis entre géométrie de tissage, alliages et formats composites.",
    cards: [{
      title: "Géométrie de tissage",
      sub: "Débit vs rétention particulaire",
      description: "La dynamique d'écoulement et le seuil de coupure dépendent directement de la stabilité de maille selon l'armure de tissage.",
      specs: ["Toile unie & sergée : Surface ouverte élevée (30–65%) pour filtration grossière à faible perte de charge.", "Toile reps unie : Serrage compact pour rétention précise de 10 à 300 µm.", "Toile reps inversée : Haute résistance mécanique en chaîne pour tamisage continu."]
    }, {
      title: "Métallurgie des alliages",
      sub: "Limites acides, chlorures et thermiques",
      description: "Choisir l'alliage approprié prévient la corrosion par piqûres et caverneuse en milieu agressif.",
      specs: ["AISI 304 / 316L : Normes industrielles générales, alimentaires et sanitaires.", "Inox au molybdène : Résistance accrue aux nettoyages chimiques acides.", "Alliages spéciaux : Solutions performantes face aux halogènes humides et acides organiques."]
    }, {
      title: "Formats structurels",
      sub: "Toile monocouche vs composites frittés",
      description: "Équilibre entre rigidité mécanique et nettoyabilité par rétrolavage entre toiles monocouches et laminés frittés.",
      specs: ["Toile monocouche : Économique, souple pour cartouches jetables et éléments plissés légers.", "Fritté 3 couches : Toile filtrante fine protégée par des couches de soutien soudées par diffusion.", "Fritté standard 5 couches : Plaque monolithique rigide résistant à l'écrasement sous flux inversé."]
    }],
    tableTitle: "Comparaison directe des spécifications",
    tableSubtitle: "Conforme aux normes de fabrication ISO 9044 / ASTM E2016",
    tableHeaders: {
      param: "Paramètre évalué",
      optA: "Option A (Monocouche / Standard)",
      optB: "Option B (Fritté / Renforcé)",
      verdict: "Verdict d'ingénierie"
    },
    tableRows: [{
      param: "Précision de filtration",
      optA: "Toile reps unie",
      optANote: "10–300 µm nominal",
      optB: "Multicouche fritté",
      optBNote: "1–200 µm de précision",
      verdict: "La toile reps offre un débit élevé ; le composite fritté confère la rigidité au rétrolavage."
    }, {
      param: "Pression de service",
      optA: "Toile simple standard",
      optANote: "Faible ΔP (non supportée)",
      optB: "Laminé fritté renforcé",
      optBNote: "Haute stabilité structurelle sous ΔP",
      verdict: "Les composites soudés par diffusion supportent les cycles de pressions différentielles élevées."
    }, {
      param: "Corrosion aux chlorures",
      optA: "Acier inoxydable AISI 304 / 304L",
      optANote: "Qualité industrielle standard",
      optB: "AISI 316L / Nuance supérieure",
      optBNote: "Nuance au molybdène",
      verdict: "Sélectionner le 316L ou une nuance supérieure évite la piqûre en présence de chlorures."
    }]
  },
  pt: {
    badge: "REFERÊNCIA DE ENGENHARIA",
    title: "Destaques da comparação técnica",
    intro: "Este guia técnico auxilia equipes de compras e engenheiros na avaliação de compromissos entre geometria de tecelagem, ligas e formatos compósitos.",
    cards: [{
      title: "Geometria de tecelagem",
      sub: "Vazão vs. retenção de partículas",
      description: "A dinâmica de fluxo e o corte de filtração dependem diretamente da estabilidade da abertura nas tramas lisa, sarja, Dutch e Dutch reversa.",
      specs: ["Lisa e sarja: Alta área aberta (30–65%) para retenção grossa com baixa perda de carga.", "Dutch lisa: Disposição compacta para retenção precisa de 10 a 300 µm.", "Dutch reversa: Alta resistência à tração para peneiramento contínuo sob esforço mecânico."]
    }, {
      title: "Metalurgia de ligas",
      sub: "Limites para ácidos, cloretos e temperatura",
      description: "A seleção correta da liga previne corrosão por pite e em frestas sob fluidos agressivos.",
      specs: ["AISI 304 / 316L : Padrões para aplicações industriais gerais, alimentícias e sanitárias.", "Inox com molibdênio: Maior resistência química em ciclos de lavagem ácida.", "Ligas especiais: Alternativas para halogênios severos e ácidos orgânicos agressivos."]
    }, {
      title: "Formatos estruturais",
      sub: "Malha simples vs. compósitos sinterizados",
      description: "Equilíbrio entre rigidez mecânica e facilidade de retrolavagem entre malha monocamada e laminados sinterizados.",
      specs: ["Malha monocamada: Econômica e flexível para elementos descartáveis e cartuchos plissados.", "Sinterizado 3 camadas: Malha fina protegida por matrizes de suporte unidas por difusão.", "Sinterizado 5 camadas: Placa monolítica rígida que resiste ao colapso sob retrolavagem."]
    }],
    tableTitle: "Comparação direta de especificações",
    tableSubtitle: "Especificações conformes a ISO 9044 / ASTM E2016",
    tableHeaders: {
      param: "Parâmetro avaliado",
      optA: "Opção A (Monocamada / Padrão)",
      optB: "Opção B (Sinterizado / Reforçado)",
      verdict: "Conclusão de engenharia"
    },
    tableRows: [{
      param: "Precisão de filtração",
      optA: "Trama Dutch lisa",
      optANote: "10–300 µm nominal",
      optB: "Multicamada sinterizada",
      optBNote: "1–200 µm de precisão",
      verdict: "A trama Dutch garante alta vazão; o sinterizado adiciona rigidez para retrolavagem."
    }, {
      param: "Pressão de trabalho",
      optA: "Malha simples padrão",
      optANote: "Baixa ΔP (sem suporte)",
      optB: "Laminado sinterizado reforçado",
      optBNote: "Alta estabilidade estrutural sob ΔP",
      verdict: "Os compósitos sinterizados por difusão suportam cargas diferenciais cíclicas severas."
    }, {
      param: "Corrosão por cloretos",
      optA: "Aço inoxidável AISI 304 / 304L",
      optANote: "Grau industrial padrão",
      optB: "AISI 316L / Grau superior",
      optBNote: "Grau com molibdênio",
      verdict: "A especificação de 316L ou graus superiores previne corrosão por pite sob cloretos."
    }]
  }
};
export default function EngineeringComparisonHighlights() {
  const {
    lang
  } = useLanguage();
  const content = pick(lang, CONTENT);
  return <section id="engineeringcomparisonhighlights" data-nav="light" className="bg-background text-foreground mf-section border-t border-border">
      <div className="mf-container">
        {/* Header Block */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="font-mono mf-caption tracking-wider border-border text-muted-foreground uppercase px-2.5 py-1 mb-4 rounded-md bg-card">
            {content.badge}
          </Badge>
          <h2 className="mf-h2 font-bold tracking-tight text-foreground font-default mb-4 text-balance">
            {content.title}
          </h2>
          <p className="mf-lead text-muted-foreground leading-relaxed">
            {content.intro}
          </p>
        </div>

        {/* 3 Pillar Highlight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16 lg:mb-20">
          {content.cards.map((card, idx) => {
          const IconComponent = idx === 0 ? Layers : idx === 1 ? ShieldCheck : Gauge;
          return <Card key={card.title} data-index={idx} className="bg-card text-card-foreground border-border rounded-lg flex flex-col justify-between">
                <CardHeader className="pb-4">
                  <div className="flex items-center justify-between gap-3 mb-2">
                    {/* Standard card icon tile */}
                    <div className="inline-flex size-10 items-center justify-center rounded-md border border-border bg-muted text-primary shrink-0">
                      <IconComponent className="size-5" aria-hidden="true" />
                    </div>
                    <span className="font-mono mf-caption text-muted-foreground font-medium">
                      0{idx + 1}
                    </span>
                  </div>
                  <CardTitle className="mf-h3 font-semibold tracking-tight text-foreground">
                    {card.title}
                  </CardTitle>
                  <CardDescription className="mf-caption uppercase tracking-wide font-mono text-primary font-medium">
                    {card.sub}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0 flex-1 flex flex-col justify-between gap-4">
                  <p className="text-sm text-muted-foreground leading-normal">
                    {card.description}
                  </p>
                  <ul className="space-y-2 border-t border-border pt-4 mt-auto">
                    {card.specs.map((spec, sIdx) => <li key={sIdx} className="mf-caption text-foreground/85 leading-relaxed flex items-start gap-2">
                        <span className="size-1.5 rounded-full bg-primary mt-1.5 shrink-0" aria-hidden="true" />
                        <span>{spec}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </Card>;
        })}
        </div>

        {/* Comparison Matrix Table */}
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="p-4 sm:p-6 border-b border-border bg-muted/40 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <ArrowRightLeft className="size-4 text-primary" aria-hidden="true" />
              <h3 className="mf-h3 font-semibold text-foreground tracking-tight">
                {content.tableTitle}
              </h3>
            </div>
            <span className="mf-caption text-muted-foreground font-mono">
              {content.tableSubtitle}
            </span>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-b border-border bg-muted/20 hover:bg-muted/20">
                  <TableHead className="w-[22%] py-3.5 px-4 font-mono mf-caption uppercase tracking-wider text-muted-foreground font-semibold">
                    {content.tableHeaders.param}
                  </TableHead>
                  <TableHead className="w-[26%] py-3.5 px-4 font-mono mf-caption uppercase tracking-wider text-muted-foreground font-semibold">
                    {content.tableHeaders.optA}
                  </TableHead>
                  <TableHead className="w-[26%] py-3.5 px-4 font-mono mf-caption uppercase tracking-wider text-muted-foreground font-semibold">
                    {content.tableHeaders.optB}
                  </TableHead>
                  <TableHead className="w-[26%] py-3.5 px-4 font-mono mf-caption uppercase tracking-wider text-muted-foreground font-semibold">
                    {content.tableHeaders.verdict}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {content.tableRows.map((row, rIdx) => <TableRow key={row.param} data-index={rIdx} className="border-b border-border hover:bg-muted/30 transition-colors">
                    {/* Evaluation Parameter */}
                    <TableCell className="py-4 px-4 align-top">
                      <span className="font-semibold text-sm text-foreground block">
                        {row.param}
                      </span>
                    </TableCell>

                    {/* Option A */}
                    <TableCell className="py-4 px-4 align-top">
                      <div className="space-y-1">
                        <span className="text-sm font-medium text-foreground block">
                          {row.optA}
                        </span>
                        <span className="inline-block mf-caption font-mono text-muted-foreground bg-muted px-2 py-0.5 rounded-sm border border-border">
                          {row.optANote}
                        </span>
                      </div>
                    </TableCell>

                    {/* Option B */}
                    <TableCell className="py-4 px-4 align-top">
                      <div className="space-y-1">
                        <span className="text-sm font-medium text-foreground block">
                          {row.optB}
                        </span>
                        <span className="inline-block mf-caption font-mono text-primary bg-muted px-2 py-0.5 rounded-sm border border-border">
                          {row.optBNote}
                        </span>
                      </div>
                    </TableCell>

                    {/* Verdict */}
                    <TableCell className="py-4 px-4 align-top">
                      <div className="flex items-start gap-2 mf-caption sm:text-sm text-foreground font-default leading-relaxed">
                        <CircleCheck className="size-4 text-primary shrink-0 mt-0.5" aria-hidden="true" />
                        <span>{row.verdict}</span>
                      </div>
                    </TableCell>
                  </TableRow>)}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>
    </section>;
}